import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { selectCurrentEmpid } from '../../../pages/login/authSlice';
import './triad.scss';
import {
    useCreateTriadResponseMutation,
    useGetTriadQuestionsQuery,
    useGetTriadResponseQuery,
    useGetTriadScoresQuery,
    useUpdateTriadMutation,
} from './triad.slice';

import { TRIAD_AUTO_SAVE_INTERVAL } from '../../../constants';
import DescriptionBox from '../../../components/descriptionBox/DescriptionBox';

interface TriadSecondTabProps {
    setTriadPageNumber: React.Dispatch<React.SetStateAction<number>>;
    triadId: number | string;
    currTriad: any;
}

export const TriadSecondTab = (props: TriadSecondTabProps) => {
    const [isAutoSave, setIsAutoSave] = useState(false);
    const autoSaveRef = useRef<any>();
    const [updateResponse, setUpdateResponse] = useState<boolean>(true);
    const [TriadResponse] = useCreateTriadResponseMutation();
    const [UpdateTriad] = useUpdateTriadMutation();
    const [finalQuestions, setFinalQuestions] = useState<any>([]);
    const tooltipRefs = useRef<{ [key: string]: any }>({});
    const lastSectionNameRef = useRef('');
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const [triadScores, setTriadScores] = useState<any>([]);

    const { data: triadQuestions } = useGetTriadQuestionsQuery();
    const { data: triadScoresData } = useGetTriadScoresQuery();
    const { data: triadResponseData } = useGetTriadResponseQuery(
        { triadId: props?.triadId },
        { skip: !props?.triadId }
    );


    useEffect(() => {
        if (triadScoresData?.data.length > 0) {
            setTriadScores(triadScoresData.data.filter((s: any) => s.triad_version_id === 1));
        }
    }, [triadScoresData]);

    useEffect(() => {
        const questionsArray: any = [];
        if (triadQuestions?.data.length > 0) {
            triadQuestions.data[0].sections.forEach((section: any) => {
                section.questions.forEach((question: any) => {
                    questionsArray.push({
                        triadSectionId: section.triad_section_id,
                        triadSectionName: section.triad_section_name,
                        questionOrder: question.question_order,
                        triadQuestionDescription: question.triad_question_description,
                        triadQuestionId: question.triad_question_id,
                        scoreId: '',
                        scoreValue: '',
                        observation: '',
                        triadResponseId: null,
                        guidanceArray: question.triadScoreGuidance,
                    });
                });
            });
        }
        setFinalQuestions(questionsArray);
    }, [triadQuestions, triadScores]);

    const handleAutoSave = () => {
        let filteredQuestions = finalQuestions.filter(
            (question: any) =>
                question.scoreId !== '' && (question.scoreId !== 4 ? question.observation !== '' : true)
        );
        if (triadResponseData?.data?.length > 0) {
            filteredQuestions = filteredQuestions.filter((question: any) => {
                const originalResponse = triadResponseData?.data?.find(
                    (response: any) => response.triad_question_id === question.triadQuestionId
                );
                return (
                    !originalResponse ||
                    question.scoreId !== originalResponse.triad_score_id ||
                    question.observation !== originalResponse.observation_comment ||
                    question.triadResponseId !== originalResponse.triad_response_id
                );
            });
        }
        const data = filteredQuestions.map((question: any) => ({
            triadId: props.triadId,
            triadQuestionId: question.triadQuestionId,
            triadScoreId: question.scoreId,
            observationComment: question.observation,
            triadResponseId: question.triadResponseId,
            value: getValueByScoreId(question.scoreId),
        }));
        if (data.length > 0) {
            TriadResponse(data)
                .unwrap()
                .then((res: any) => {
                    if (res.statusCode === 200 && res.data) {
                        setIsAutoSave(true);
                    } else {
                        toast.error('Auto-save failed');
                    }
                })
                .catch((err: any) => {
                    toast.error(`${err} during Auto-save`);
                });
        }
    };

    useEffect(() => {
        autoSaveRef.current = setInterval(() => {
            handleAutoSave();
        }, TRIAD_AUTO_SAVE_INTERVAL);

        return () => {
            clearInterval(autoSaveRef.current);
        };
    }, [finalQuestions, triadResponseData]);

    useEffect(() => {
        if (triadResponseData?.data?.length > 0) {
            if (updateResponse || isAutoSave) {
                let updateNeeded = false;

                const updatedQuestions = finalQuestions.map((question: any) => {
                    const matchingResponse = triadResponseData.data.find((response: any) => {
                        return question.triadQuestionId === response.triad_question_id;
                    });

                    if (matchingResponse) {
                        if (
                            question.scoreId !== matchingResponse.triad_score_id ||
                            question.observation !== matchingResponse.observation_comment ||
                            question.triadResponseId !== matchingResponse.triad_response_id
                        ) {
                            updateNeeded = true;
                        }
                        return {
                            ...question,
                            scoreId: matchingResponse.triad_score_id,
                            observation: matchingResponse.observation_comment,
                            triadResponseId: matchingResponse.triad_response_id,
                        };
                    } else {
                        return question;
                    }
                });

                if (updateNeeded) {
                    setFinalQuestions(updatedQuestions);
                }
            }
            setIsAutoSave(false);
        }
    }, [triadResponseData, finalQuestions]);

    const handleUpdates = (e: any, index: number, key: any) => {
        if (key === 'scoreId') {
            setFinalQuestions((prev: any) => {
                const updatedQuestions = [...prev];
                if (index >= 0 && index < updatedQuestions.length) {
                    updatedQuestions[index].scoreId = e.target.value;
                }
                return updatedQuestions;
            });
        }
        if (key === 'observation') {
            setFinalQuestions((prev: any) => {
                const updatedQuestions = [...prev];
                if (index >= 0 && index < updatedQuestions.length) {
                    updatedQuestions[index].observation = e.target.value;
                }
                return updatedQuestions;
            });
        }
        setUpdateResponse(false);
    };

    const getValueByScoreId = (scoreId: string): number => {
        const point = triadScores.find((score: any) => score.triad_score_id === scoreId);
        return point?.score_value ?? 0;
    };

    const handleSave = () => {
        const hasMissingValues = finalQuestions.some((question: any) => {
            if (question.scoreId === 4) {
                return false;
            }
            return question.triadQuestionId === '' || question.scoreId === '' || question.observation === '';
        });

        if (hasMissingValues) {
            toast.error('Please fill all the required fields');
            return;
        }
        let filteredQuestions: any[] = [];
        if (triadResponseData?.data?.length > 0) {
            // to update only the modified responses
            filteredQuestions = finalQuestions.filter((question: any) => {
                const originalResponse = triadResponseData?.data?.find(
                    (response: any) => response.triad_question_id === question.triadQuestionId
                );
                return (
                    !originalResponse ||
                    question.scoreId !== originalResponse.triad_score_id ||
                    question.observation !== originalResponse.observation_comment ||
                    question.triadResponseId !== originalResponse.triad_response_id
                );
            });
        } else {
            // to save all the responses for the first time
            filteredQuestions = [...finalQuestions];
        }
        const filteredData = filteredQuestions.map((question: any) => {
            const value = getValueByScoreId(question.scoreId);
            return {
                triadId: props.triadId,
                triadQuestionId: question.triadQuestionId,
                triadScoreId: question.scoreId,
                observationComment: question.observation,
                triadResponseId: question.triadResponseId,
                value,
            };
        });
        const data = finalQuestions.map((question: any) => {
            const value = getValueByScoreId(question.scoreId);
            return {
                triadId: props.triadId,
                triadQuestionId: question.triadQuestionId,
                triadScoreId: question.scoreId,
                observationComment: question.observation,
                triadResponseId: question.triadResponseId,
                value,
            };
        });
        // Calculate average value
        const totalValue = data.reduce((acc: any, item: any) => acc + item.value, 0);
        const totalQuestions = data.filter((item: any) => item.value > 0).length * 3;
        const averageValue = totalQuestions > 0 ? (totalValue / totalQuestions).toFixed(2) : null;
        if (!updateResponse) {
            if (filteredData.length > 0) {
                TriadResponse(filteredData)
                    .unwrap()
                    .then((res: any) => {
                        if (res.statusCode === 200 && res.data) {
                            props?.setTriadPageNumber(3);
                            toast.success('Coaching Observations Response save Successful');
                        } else {
                            toast.error('Coaching Observations Respone save Failed');
                        }
                    })
                    .catch((err: any) => {
                        toast.error(err);
                    });
            }
            const obj = {
                triadId: props.triadId,
                assessmentScore: averageValue,
                acknowledged: props?.currTriad?.acknowledged ? props?.currTriad?.acknowledged : false,
            };
            UpdateTriad(obj)
                .unwrap()
                .then((res: any) => {
                    if (res.statusCode === 200 && res.data) {
                        props?.setTriadPageNumber(3);
                        toast.success('Coaching Observations save Successful');
                    } else {
                        toast.error('Coaching Observations save Failed');
                    }
                })
                .catch((err: any) => {
                    toast.error(err);
                });
        } else {
            props?.setTriadPageNumber(3);
        }
    };
    const renderRadio = (question: any, index: number): React.ReactNode => {
        if (triadScores?.length > 0) {
            return (
                <div className="flex justify-content-center">
                    <div className="flex flex-column gap-4">
                        {triadScores?.map((item: any) => {
                            return (
                                <div key={item?.triad_score_id} className="flex align-items-center">
                                    <div className="row">
                                        <div className="col-md-1">
                                            <RadioButton
                                                inputId={item?.triad_score_id}
                                                name={question?.triadQuestionId}
                                                value={item.triad_score_id}
                                                onChange={(e) => handleUpdates(e, index, 'scoreId')}
                                                checked={question?.scoreId === item?.triad_score_id}
                                                disabled={
                                                    props?.currTriad?.employee_number === loggedEmpNum ||
                                                    props?.currTriad?.acknowledged ||
                                                    (props?.currTriad?.month &&
                                                        props?.currTriad?.month !==
                                                        new Date().getMonth() + 1) ||
                                                    (props?.currTriad?.year &&
                                                        props?.currTriad?.year !== new Date().getFullYear())
                                                }
                                                className={`rounded-start ${question.scoreId ? '' : 'p-invalid'
                                                    }`}
                                            />
                                        </div>
                                        <div className="col-md-8">
                                            <label htmlFor={item?.triad_score_id}>
                                                {item?.triad_score_name}
                                            </label>
                                        </div>
                                        <div className="col-md-1">
                                            {question.guidanceArray?.map((que: any) => {
                                                if (que.guidance_score_id === item?.triad_score_id) {
                                                    const tooltipId = `tooltip-target-${que.triad_score_guidance_id}`;
                                                    return (
                                                        <React.Fragment key={que.triad_score_guidance_id}>
                                                            <div
                                                                id={tooltipId}
                                                                className="triad-question-circle"
                                                                onMouseEnter={(e) =>
                                                                    tooltipRefs.current[tooltipId]?.show(e)
                                                                }
                                                                onMouseLeave={(e) =>
                                                                    tooltipRefs.current[tooltipId]?.hide(e)
                                                                }
                                                            >
                                                                <i
                                                                    className="pi pi-question"
                                                                    style={{ color: 'white' }}
                                                                />
                                                            </div>

                                                            <Tooltip
                                                                ref={(el) => {
                                                                    tooltipRefs.current[tooltipId] = el;
                                                                }}
                                                                target={`#${tooltipId}`}
                                                            >
                                                                <p
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: que.triad_score_guidance_description,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </React.Fragment>
                                                    );
                                                } else return <div />;
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        } else return <div />;
    };

    return (
        <div>
            <h3>Coaching Assessment</h3>
            <h5>Coaching Effectiveness Assessment form</h5>
            {finalQuestions &&
                finalQuestions.map((question: any, index: number) => {
                    const isFirstQuestionInSection = lastSectionNameRef.current !== question.triadSectionName;
                    lastSectionNameRef.current = question.triadSectionName;

                    const rowClass = index % 2 === 0 ? 'row-even' : 'row-odd';
                    const isReadOnly =
                        props?.currTriad?.employee_number === loggedEmpNum ||
                        props?.currTriad?.acknowledged ||
                        (props?.currTriad?.month && props?.currTriad?.month !== new Date().getMonth() + 1) ||
                        (props?.currTriad?.year && props?.currTriad?.year !== new Date().getFullYear());

                    return (
                        <React.Fragment key={question?.triad_question_id}>
                            <div className="row" style={{ marginTop: '10px' }}>
                                {isFirstQuestionInSection && (
                                    <div className="col-md-4" style={{ fontWeight: 'bolder' }}>
                                        {question.triadSectionName}
                                    </div>
                                )}
                                {!isFirstQuestionInSection && <div className="col-md-4" />}
                                {index === 0 && (
                                    <div className="col-md-3" style={{ fontWeight: 'bolder' }}>
                                        {'Score'}
                                    </div>
                                )}
                                {index === 0 && (
                                    <div className="col-md-5" style={{ fontWeight: 'bolder' }}>
                                        {'Specific Observation'}
                                    </div>
                                )}
                            </div>
                            <div className={`row ${rowClass}`}>
                                <div className="col-md-4" style={{ fontWeight: 'normal' }}>
                                    {question.triadQuestionDescription}
                                </div>
                                <div className="col-md-3">{renderRadio(question, index)}</div>
                                <div className="col-md-5">
                                    <DescriptionBox
                                        readOnly={isReadOnly}
                                        value={question.observation}
                                        responseDescription={question.observation}
                                        limit={2000}
                                        className={`${question.observation || question.scoreId === 4 ? '' : 'p-invalid'
                                            }`}
                                        onChange={(value) => {
                                            const syntheticEvent = {
                                                target: { value },
                                            };
                                            handleUpdates(syntheticEvent, index, 'observation');
                                        }}
                                    />
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            {
                <div className="button-container-triad">
                    <Button
                        onClick={() => props?.setTriadPageNumber(1)}
                        label="Back"
                        className="btn-secondary"
                    />
                    <Button onClick={handleSave} label="Next" className="btn-primary" />
                </div>
            }
        </div>
    );
};
